import React, { useState } from 'react';
import { 
  X, Zap, Brain, Microscope, BarChart3, 
  Upload, Settings, Scale, Target, 
  Filter, Minimize2, ChevronRight,
  Sparkles, Clock, Users, Eye
} from 'lucide-react';

const PipelineTemplates = ({ onSelectTemplate, onClose, onCreateBlank }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showNameModal, setShowNameModal] = useState(false);
  const [pipelineName, setPipelineName] = useState('');
  const [pipelineDescription, setPipelineDescription] = useState('');

  const templates = [
    {
      id: 'quick_start',
      name: 'Quick Start',
      description: 'Perfect for beginners - upload data and train a model in 3 simple steps',
      difficulty: 'Beginner',
      estimatedTime: '5-10 min',
      icon: Zap,
      color: 'from-green-500 to-emerald-600',
      nodes: [
        { type: 'uploadDataset', icon: Upload, label: 'Upload Dataset' },
        { type: 'configureFeatures', icon: Settings, label: 'Configure Features' },
        { type: 'trainModel', icon: Brain, label: 'Train Model' }
      ],
      elements: [
        {
          id: 'upload-1',
          type: 'uploadDataset',
          position: { x: 100, y: 100 },
          data: {
            label: 'Upload Dataset',
            parameters: { file: null },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'configure-1',
          type: 'configureFeatures',
          position: { x: 400, y: 100 },
          data: {
            label: 'Configure Features',
            parameters: { feature_columns: [], target_column: '' },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'train-1',
          type: 'trainModel',
          position: { x: 700, y: 100 },
          data: {
            label: 'Train Model',
            parameters: { 
              model_type: 'random_forest',
              test_size: 0.2,
              random_state: 42,
              n_estimators: 100,
              max_depth: 10
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'edge-upload-configure',
          source: 'upload-1',
          target: 'configure-1',
          type: 'default'
        },
        {
          id: 'edge-configure-train',
          source: 'configure-1',
          target: 'train-1',
          type: 'default'
        }
      ]
    },
    {
      id: 'data_analysis',
      name: 'Complete Data Analysis',
      description: 'Comprehensive pipeline with data preprocessing and model training',
      difficulty: 'Intermediate',
      estimatedTime: '15-20 min',
      icon: BarChart3,
      color: 'from-blue-500 to-cyan-600',
      nodes: [
        { type: 'uploadDataset', icon: Upload, label: 'Upload Dataset' },
        { type: 'configureFeatures', icon: Settings, label: 'Configure Features' },
        { type: 'impute', icon: Filter, label: 'Handle Missing Data' },
        { type: 'scale', icon: Scale, label: 'Scale Features' },
        { type: 'trainModel', icon: Brain, label: 'Train Model' }
      ],
      elements: [
        {
          id: 'upload-1',
          type: 'uploadDataset',
          position: { x: 50, y: 100 },
          data: {
            label: 'Upload Dataset',
            parameters: { file: null },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'configure-1',
          type: 'configureFeatures',
          position: { x: 250, y: 100 },
          data: {
            label: 'Configure Features',
            parameters: { feature_columns: [], target_column: '' },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'impute-1',
          type: 'impute',
          position: { x: 450, y: 100 },
          data: {
            label: 'Handle Missing Data',
            parameters: { 
              numeric_impute_strategy: 'mean',
              categorical_impute_strategy: 'most_frequent'
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'scale-1',
          type: 'scale',
          position: { x: 650, y: 100 },
          data: {
            label: 'Scale Features',
            parameters: { 
              method: 'standard',
              feature_range_min: 0.0,
              feature_range_max: 1.0
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'train-1',
          type: 'trainModel',
          position: { x: 850, y: 100 },
          data: {
            label: 'Train Model',
            parameters: { 
              model_type: 'random_forest',
              test_size: 0.2,
              random_state: 42,
              n_estimators: 100,
              max_depth: 10
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'edge-upload-configure',
          source: 'upload-1',
          target: 'configure-1',
          type: 'default'
        },
        {
          id: 'edge-configure-impute',
          source: 'configure-1',
          target: 'impute-1',
          type: 'default'
        },
        {
          id: 'edge-impute-scale',
          source: 'impute-1',
          target: 'scale-1',
          type: 'default'
        },
        {
          id: 'edge-scale-train',
          source: 'scale-1',
          target: 'train-1',
          type: 'default'
        }
      ]
    },
    {
      id: 'feature_engineering',
      name: 'Advanced Feature Engineering',
      description: 'Advanced pipeline with feature selection and dimensionality reduction',
      difficulty: 'Advanced',
      estimatedTime: '20-30 min',
      icon: Target,
      color: 'from-purple-500 to-pink-600',
      nodes: [
        { type: 'uploadDataset', icon: Upload, label: 'Upload Dataset' },
        { type: 'configureFeatures', icon: Settings, label: 'Configure Features' },
        { type: 'impute', icon: Filter, label: 'Handle Missing Data' },
        { type: 'encode', icon: Settings, label: 'Encode Categories' },
        { type: 'selectFeatures', icon: Target, label: 'Feature Selection' },
        { type: 'scale', icon: Scale, label: 'Scale Features' },
        { type: 'trainModel', icon: Brain, label: 'Train Model' }
      ],
      elements: [
        {
          id: 'upload-1',
          type: 'uploadDataset',
          position: { x: 50, y: 50 },
          data: {
            label: 'Upload Dataset',
            parameters: { file: null },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'configure-1',
          type: 'configureFeatures',
          position: { x: 250, y: 50 },
          data: {
            label: 'Configure Features',
            parameters: { feature_columns: [], target_column: '' },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'impute-1',
          type: 'impute',
          position: { x: 450, y: 50 },
          data: {
            label: 'Handle Missing Data',
            parameters: { 
              numeric_impute_strategy: 'mean',
              categorical_impute_strategy: 'most_frequent'
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'encode-1',
          type: 'encode',
          position: { x: 250, y: 200 },
          data: {
            label: 'Encode Categories',
            parameters: { 
              method: 'onehot',
              handle_unknown: 'ignore',
              ordinal_categories: {}
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'selectFeatures-1',
          type: 'selectFeatures',
          position: { x: 450, y: 200 },
          data: {
            label: 'Feature Selection',
            parameters: { 
              method: 'variance',
              threshold: 0.0,
              k: 10,
              percentile: 75
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'scale-1',
          type: 'scale',
          position: { x: 650, y: 200 },
          data: {
            label: 'Scale Features',
            parameters: { 
              method: 'standard',
              feature_range_min: 0.0,
              feature_range_max: 1.0
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'train-1',
          type: 'trainModel',
          position: { x: 850, y: 125 },
          data: {
            label: 'Train Model',
            parameters: { 
              model_type: 'random_forest',
              test_size: 0.2,
              random_state: 42,
              n_estimators: 100,
              max_depth: 10
            },
            status: 'pending',
            results: null
          }
        },
        // Edges
        {
          id: 'edge-upload-configure',
          source: 'upload-1',
          target: 'configure-1',
          type: 'default'
        },
        {
          id: 'edge-configure-impute',
          source: 'configure-1',
          target: 'impute-1',
          type: 'default'
        },
        {
          id: 'edge-impute-encode',
          source: 'impute-1',
          target: 'encode-1',
          type: 'default'
        },
        {
          id: 'edge-encode-select',
          source: 'encode-1',
          target: 'selectFeatures-1',
          type: 'default'
        },
        {
          id: 'edge-select-scale',
          source: 'selectFeatures-1',
          target: 'scale-1',
          type: 'default'
        },
        {
          id: 'edge-scale-train',
          source: 'scale-1',
          target: 'train-1',
          type: 'default'
        }
      ]
    },
    {
      id: 'research_pipeline',
      name: 'Research Pipeline',
      description: 'Complete research-grade pipeline with all preprocessing steps',
      difficulty: 'Expert',
      estimatedTime: '30-45 min',
      icon: Microscope,
      color: 'from-orange-500 to-red-600',
      nodes: [
        { type: 'uploadDataset', icon: Upload, label: 'Upload Dataset' },
        { type: 'configureFeatures', icon: Settings, label: 'Configure Features' },
        { type: 'impute', icon: Filter, label: 'Handle Missing Data' },
        { type: 'encode', icon: Settings, label: 'Encode Categories' },
        { type: 'scale', icon: Scale, label: 'Scale Features' },
        { type: 'selectFeatures', icon: Target, label: 'Feature Selection' },
        { type: 'reduceDimensions', icon: Minimize2, label: 'Reduce Dimensions' },
        { type: 'trainModel', icon: Brain, label: 'Train Model' }
      ],
      elements: [
        {
          id: 'upload-1',
          type: 'uploadDataset',
          position: { x: 50, y: 100 },
          data: {
            label: 'Upload Dataset',
            parameters: { file: null },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'configure-1',
          type: 'configureFeatures',
          position: { x: 200, y: 100 },
          data: {
            label: 'Configure Features',
            parameters: { feature_columns: [], target_column: '' },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'impute-1',
          type: 'impute',
          position: { x: 350, y: 100 },
          data: {
            label: 'Handle Missing Data',
            parameters: { 
              numeric_impute_strategy: 'mean',
              categorical_impute_strategy: 'most_frequent'
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'encode-1',
          type: 'encode',
          position: { x: 500, y: 100 },
          data: {
            label: 'Encode Categories',
            parameters: { 
              method: 'onehot',
              handle_unknown: 'ignore',
              ordinal_categories: {}
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'scale-1',
          type: 'scale',
          position: { x: 650, y: 100 },
          data: {
            label: 'Scale Features',
            parameters: { 
              method: 'standard',
              feature_range_min: 0.0,
              feature_range_max: 1.0
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'selectFeatures-1',
          type: 'selectFeatures',
          position: { x: 350, y: 250 },
          data: {
            label: 'Feature Selection',
            parameters: { 
              method: 'variance',
              threshold: 0.0,
              k: 10,
              percentile: 75
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'reduceDimensions-1',
          type: 'reduceDimensions',
          position: { x: 500, y: 250 },
          data: {
            label: 'Reduce Dimensions',
            parameters: { 
              method: 'pca',
              n_components: 2,
              random_state: 42
            },
            status: 'pending',
            results: null
          }
        },
        {
          id: 'train-1',
          type: 'trainModel',
          position: { x: 650, y: 250 },
          data: {
            label: 'Train Model',
            parameters: { 
              model_type: 'random_forest',
              test_size: 0.2,
              random_state: 42,
              n_estimators: 100,
              max_depth: 10
            },
            status: 'pending',
            results: null
          }
        },
        // Edges
        {
          id: 'edge-upload-configure',
          source: 'upload-1',
          target: 'configure-1',
          type: 'default'
        },
        {
          id: 'edge-configure-impute',
          source: 'configure-1',
          target: 'impute-1',
          type: 'default'
        },
        {
          id: 'edge-impute-encode',
          source: 'impute-1',
          target: 'encode-1',
          type: 'default'
        },
        {
          id: 'edge-encode-scale',
          source: 'encode-1',
          target: 'scale-1',
          type: 'default'
        },
        {
          id: 'edge-scale-select',
          source: 'scale-1',
          target: 'selectFeatures-1',
          type: 'default'
        },
        {
          id: 'edge-select-reduce',
          source: 'selectFeatures-1',
          target: 'reduceDimensions-1',
          type: 'default'
        },
        {
          id: 'edge-reduce-train',
          source: 'reduceDimensions-1',
          target: 'train-1',
          type: 'default'
        }
      ]
    }
  ];

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setPipelineName(template.name); // Pre-fill with template name
    setPipelineDescription(template.description);
    setShowNameModal(true);
  };

  const handleConfirmTemplate = (e) => {
    e.preventDefault();
    if (!pipelineName.trim()) return;
    
    onSelectTemplate({
      ...selectedTemplate,
      name: pipelineName.trim(),
      description: pipelineDescription.trim()
    });
    setShowNameModal(false);
  };

  const handleCloseNameModal = () => {
    setShowNameModal(false);
    setPipelineName('');
    setPipelineDescription('');
    setSelectedTemplate(null);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'bg-green-100 text-green-700';
      case 'Intermediate': return 'bg-blue-100 text-blue-700';
      case 'Advanced': return 'bg-purple-100 text-purple-700';
      case 'Expert': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const TemplateCard = ({ template, isSelected, onClick }) => {
    const Icon = template.icon;
    
    return (
      <div
        onClick={() => onClick(template)}
        className={`cursor-pointer transition-all duration-200 rounded-xl border-2 p-6 hover:shadow-lg ${
          isSelected 
            ? 'border-blue-500 bg-blue-50 shadow-lg' 
            : 'border-gray-200 bg-white hover:border-gray-300'
        }`}
      >
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-lg bg-gradient-to-r ${template.color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(template.difficulty)}`}>
            {template.difficulty}
          </span>
        </div>

        <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
        <p className="text-sm text-gray-600 mb-4 line-clamp-2">{template.description}</p>

        <div className="flex items-center justify-between text-xs text-gray-500 mb-4">
          <div className="flex items-center space-x-1">
            <Clock size={14} />
            <span>{template.estimatedTime}</span>
          </div>
          <div className="flex items-center space-x-1">
            <Users size={14} />
            <span>{template.nodes.length} nodes</span>
          </div>
        </div>

        {/* Mini Node Preview */}
        <div className="flex items-center space-x-2 overflow-x-auto pb-2">
          {template.nodes.map((node, index) => (
            <div key={index} className="flex items-center space-x-1 flex-shrink-0">
              <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                <node.icon size={14} className="text-gray-600" />
              </div>
              {index < template.nodes.length - 1 && (
                <ChevronRight size={12} className="text-gray-400" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const DetailedPreview = ({ template }) => {
    return (
      <div className="bg-gray-50 rounded-xl p-6">
        <h4 className="font-semibold text-gray-900 mb-4">Pipeline Preview</h4>
        <div className="space-y-3">
          {template.nodes.map((node, index) => (
            <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded-lg">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                <node.icon size={16} className="text-gray-600" />
              </div>
              <div className="flex-1">
                <div className="font-medium text-sm text-gray-900">{node.label}</div>
                <div className="text-xs text-gray-500">Step {index + 1}</div>
              </div>
              {index < template.nodes.length - 1 && (
                <ChevronRight size={16} className="text-gray-400" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                <Sparkles className="w-6 h-6 text-blue-500" />
                <span>Choose a Pipeline Template</span>
              </h2>
              <p className="text-gray-600 mt-1">Get started quickly with pre-built pipeline templates</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Templates Grid */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {templates.map((template) => (
                  <TemplateCard
                    key={template.id}
                    template={template}
                    isSelected={selectedTemplate?.id === template.id}
                    onClick={setSelectedTemplate}
                  />
                ))}
              </div>
            </div>

            {/* Preview Panel */}
            <div className="lg:col-span-1">
              {selectedTemplate ? (
                <DetailedPreview template={selectedTemplate} />
              ) : (
                <div className="bg-gray-50 rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Eye size={24} className="text-gray-400" />
                  </div>
                  <p className="text-gray-500">Select a template to see the preview</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <button
            onClick={onCreateBlank}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 text-sm font-medium"
          >
            Start with blank pipeline
          </button>
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => selectedTemplate && handleTemplateSelect(selectedTemplate)}
              disabled={!selectedTemplate}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              Use This Template
            </button>
          </div>
        </div>

        {/* Pipeline Name Modal */}
        {showNameModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
            <div className="bg-white rounded-xl p-6 w-full max-w-md mx-4">
              <h3 className="text-xl font-bold mb-4">Name Your Pipeline</h3>
              <form onSubmit={handleConfirmTemplate}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Pipeline Name
                    </label>
                    <input
                      type="text"
                      value={pipelineName}
                      onChange={(e) => setPipelineName(e.target.value)}
                      placeholder="Enter pipeline name"
                      className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      required
                      autoFocus
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description (Optional)
                    </label>
                    <textarea
                      value={pipelineDescription}
                      onChange={(e) => setPipelineDescription(e.target.value)}
                      placeholder="Enter pipeline description"
                      rows={3}
                      className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={handleCloseNameModal}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 text-sm font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                  >
                    Create Pipeline
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PipelineTemplates;